import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs/operators';
import { CreateProjectService } from '../services/create-project.service';
import { NotificationService } from '../services/notification.service';
import { RoleViewService } from '../services/role-view.service';
import { OpenAddCustomDepartmentDialogComponent } from './open-add-custom-department-dialog/open-add-custom-department-dialog.component';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
interface DepartmentMember {
  id: string;
  name: string;
  email: string;
  role: string;
  raw: any;
}

interface DepartmentVM {
  id: string;
  name: string;
  memberCount: number;
  isFavorite: boolean;
  createdAt: string | null;
  deletedAt: string | null;
  expanded: boolean;
  membersLoaded: boolean;
  loadingMembers: boolean;
  members: DepartmentMember[];
  _updating?: boolean;
  customSelectedDept: CustomSelectedDept[];
}

interface CustomSelectedDept {
  flag: boolean;
  deptname: string;
  selectedMembers:[];
}

interface EditDialogResult {
  name?: string;
  members?: any[];
  payload?: { custom_selected_dept?: any[] };
}

@Component({
  selector: 'app-manage-custom-department',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatExpansionModule, MatDialogModule, MatTooltipModule, TutorialVideoWidgetComponent],
  templateUrl: './manage-custom-department.component.html',
  styleUrls: ['./manage-custom-department.component.css']
})
export class ManageCustomDepartmentComponent implements OnInit {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  departments: DepartmentVM[] = [];
  // Archived departments are held client-side until a backend archive API is available.
  archivedDepartments: DepartmentVM[] = [];
  showDeleted = false;
  searchText = '';
  isLoading = false;
  isProcessing = false;
  isLoadingArchived = false;
  activeDepartmentCount = 0;
  archivedDepartmentCount = 0;
  private archivedLoaded = false;

  userRole = 'superAdmin';
  workDayId = '';
  userName = '';
  scrHeight = '';

  showDeleteConfirmModal = false;
  deleteConfirmTitle = '';
  private pendingArchiveDept: DepartmentVM | null = null;

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: Event) {
    this.scrHeight = `${window.innerHeight - 100}px`;
  }

  constructor(
    private readonly createProjectService: CreateProjectService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog,
    public readonly roleviewservice: RoleViewService
  ) {
    this.getScreenSize();
  }

  ngOnInit(): void {
    this.getUserInfo();
    this.loadDepartmentsFromAPI();
  }

  getUserInfo(): void {
    this.workDayId = this.roleviewservice.getWorkdayId() || '';
    this.userRole = this.roleviewservice.getActualRole();
    this.userName = this.roleviewservice.getUserName();
  }

  loadDepartmentsFromAPI(): void {
    this.isLoading = true;
    this.createProjectService.fetchCustomDepartmentNames('active')
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe({
        next: (resp: any) => {
          const payload = resp?.payload || [];
          this.departments = payload
            // .filter((d: any) => this.isCustomDepartment(d))
            .map((d: any) => this.mapDepartment(d));
          this.activeDepartmentCount = Number(resp?.active_department_count ?? this.departments.length);
          if (resp?.archived_department_count !== undefined) {
            this.archivedDepartmentCount = Number(resp.archived_department_count);
          }
        },
        error: (error: any) => {
          console.trace('Error loading departments:', error);
          this.notificationService.showError('Error loading departments');
        }
      });
  }

  // Switches the Active/Archived tab, only fetching the archived list the first time
  // it's actually opened instead of loading both lists eagerly on init.
  setShowDeleted(showDeleted: boolean): void {
    this.showDeleted = showDeleted;
    if (showDeleted && !this.archivedLoaded && !this.isLoadingArchived) {
      this.loadArchivedDepartmentsFromAPI();
    }
  }

  private loadArchivedDepartmentsFromAPI(): void {
    this.isLoadingArchived = true;
    this.createProjectService.fetchCustomDepartmentNames('archive')
      .pipe(finalize(() => { this.isLoadingArchived = false; }))
      .subscribe({
        next: (resp: any) => {
          const payload = resp?.payload || [];
          this.archivedDepartments = payload
            // .filter((d: any) => this.isCustomDepartment(d))
            .map((d: any) => {
              const dept = this.mapDepartment(d);
              dept.deletedAt = d.archived_at || d.deleted_at || d.updated_at || null;
              return dept;
            });
          this.archivedDepartmentCount = Number(resp?.archived_department_count ?? this.archivedDepartments.length);
          this.archivedLoaded = true;
        },
        error: (error: any) => {
          console.trace('Error loading archived departments:', error);
        }
      });
  }

  // private isCustomDepartment(d: any): boolean {
  //   return String(d?.dept_id || '').trim().toUpperCase().startsWith('CUST');
  // }

  private mapDepartment(d: any): DepartmentVM {
    const members = this.mapMembersFromDepartment(d);
    return {
      id: String(d.dept_id),
      name: String(d.department_name || ''),
      memberCount: members.length || Number(d.team_member_count || 0),
      isFavorite: this.toBooleanFlag(d.is_favorite),
      createdAt: d.created_at || null,
      deletedAt: null,
      expanded: false,
      membersLoaded: true,
      loadingMembers: false,
      members,
      customSelectedDept:d.custom_selected_dept
    };
  }

  // Members now arrive with the list under custom_selected_dept groups; flatten and dedupe them.
  private mapMembersFromDepartment(d: any): DepartmentMember[] {
    const groups = Array.isArray(d?.custom_selected_dept) ? d.custom_selected_dept : [];
    const seen = new Set<string>();
    const members: DepartmentMember[] = [];
    for (const group of groups) {
      const entries = group?.selected_members ?? group?.selectedMembers ?? group?.members ?? [];
      for (const entry of Array.isArray(entries) ? entries : []) {
        if (!entry || typeof entry !== 'object') { continue; }
        const member = this.mapMember(entry);
        if (member.id && !seen.has(member.id)) {
          seen.add(member.id);
          members.push(member);
        }
      }
    }
    return members;
  }

  editDepartment(dept: DepartmentVM, event?: Event): void {
    event?.stopPropagation();
    if (dept.membersLoaded || dept.memberCount === 0) {
      this.openDepartmentDialog(dept);
      return;
    }
    this.isProcessing = true;
    this.createProjectService.fetchTeamMembers(dept.id, this.workDayId)
      .pipe(finalize(() => { this.isProcessing = false; }))
      .subscribe({
        next: (members: any[]) => {
          dept.members = (members || []).map((m: any) => this.mapMember(m));
          dept.memberCount = dept.members.length;
          dept.membersLoaded = true;
          this.openDepartmentDialog(dept);
        },
        error: (error: any) => {
          console.trace('Error loading team members:', error);
          this.notificationService.showError('Error loading team members');
        }
      });
  }
                   

  private toBooleanFlag(value: any): boolean {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return value === true;
  }

  setSearch(val: string): void {
    this.searchText = val;
  }

  onPanelOpened(dept: DepartmentVM): void {
    dept.expanded = true;
  }

  private mapMember(m: any): DepartmentMember {
    return {
      id: String(m.tm_id ?? m.tm_workday_id ?? m.tm_email ?? m.email ?? m.email_addresses ?? ''),
      name: String(m.tm_name || m.name || ''),
      email: String(m.tm_email || m.email || m.tm_mail || m.email_addresses || ''),
      role: String(m.tm_designation || m.job_title || ''),
      raw: m
    };
  }

  openAddDialog(): void {
    this.openDepartmentDialog(null);
  }

  private openDepartmentDialog(dept: DepartmentVM | null): void {
    const existingDepartmentNames = [
      ...this.departments.map(d => d.name),
      ...this.archivedDepartments.map(d => d.name)
    ];

    const dialogRef = this.dialog.open(OpenAddCustomDepartmentDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: dept
        ? {
          deptId: dept.id,
          name: dept.name,
          members: dept.members.map(m => m.raw),
          customSelectedDept: dept.customSelectedDept,
          existingDepartmentNames
        }
        : { existingDepartmentNames }
    });
    dialogRef.afterClosed().subscribe((result: EditDialogResult | undefined) => {
      if (!result?.name) { return; }
      if (dept) {
        this.notificationService.showSuccess('Department updated successfully!');
         this.loadDepartmentsFromAPI();
      } else {
        this.notificationService.showSuccess('Department created successfully!');
        this.loadDepartmentsFromAPI();
      }
    });
  }

  toggleFavorite(dept: DepartmentVM, event?: Event): void {
    event?.stopPropagation();
    if (dept._updating) { return; }
    const previous = dept.isFavorite;
    dept.isFavorite = !dept.isFavorite;
    dept._updating = true;
    const body = {
      departmentId: dept.id,
      updatedBy: this.workDayId,
      isFavType: 'Department' as const
    };
    this.createProjectService.updateFavoriteDepartment(body).subscribe({
      next: () => { dept._updating = false; },
      error: (error: any) => {
        dept.isFavorite = previous;
        dept._updating = false;
        console.trace('Error updating favorite department:', error);
        this.notificationService.showError('Error updating favorite');
      }
    });
  }

  archiveDepartment(dept: DepartmentVM, event?: Event): void {
    event?.stopPropagation();
    this.pendingArchiveDept = dept;
    this.deleteConfirmTitle = dept.name;
    this.showDeleteConfirmModal = true;
  }

  confirmDelete(): void {
    const dept = this.pendingArchiveDept;
    if (!dept) {
      this.cancelDelete();
      return;
    }

    this.isProcessing = true;
    this.createProjectService.deleteCustomDepartment([{ deptId: dept.id, isdeleted: true }])
      .pipe(finalize(() => { this.isProcessing = false; }))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess('Department deleted successfully!');
          this.cancelDelete();
          this.loadDepartmentsFromAPI();
          if (this.archivedLoaded) { this.loadArchivedDepartmentsFromAPI(); }
        },
        error: (error: any) => {
          console.trace('Error deleting department:', error);
          this.notificationService.showError('Error deleting department');
          this.cancelDelete();
        }
      });
  }

  cancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.deleteConfirmTitle = '';
    this.pendingArchiveDept = null;
  }

  restoreDepartment(dept: DepartmentVM, event?: Event): void {
    event?.stopPropagation();
    this.isProcessing = true;
    this.createProjectService.deleteCustomDepartment([{ deptId: dept.id, isdeleted: false }])
      .pipe(finalize(() => { this.isProcessing = false; }))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess('Department restored successfully!');
          this.loadDepartmentsFromAPI();
          this.loadArchivedDepartmentsFromAPI();
        },
        error: (error: any) => {
          console.trace('Error restoring department:', error);
          this.notificationService.showError('Error restoring department');
        }
      });
  }

  filteredDepartments(): DepartmentVM[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.departments;
    }
    return this.departments.filter(d =>
      d.name.toLowerCase().includes(term) ||
      d.members.some(m => m.name.toLowerCase().includes(term) || m.email.toLowerCase().includes(term))
    );
  }

  filteredMembers(dept: DepartmentVM): DepartmentMember[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return dept.members;
    }
    return dept.members.filter(m =>
      m.name.toLowerCase().includes(term) || m.email.toLowerCase().includes(term)
    );
  }

  filteredDeletedDepartments(): DepartmentVM[] {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      return this.archivedDepartments;
    }
    return this.archivedDepartments.filter(d => d.name.toLowerCase().includes(term));
  }

  getActiveCount(): number {
    return this.activeDepartmentCount;
  }

  getArchivedCount(): number {
    return this.archivedDepartmentCount;
  }

  getInitials(name: string): string {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  }

  trackByDeptId(_index: number, dept: DepartmentVM): string {
    return dept.id;
  }

  trackByMemberId(_index: number, member: DepartmentMember): string {
    return member.id;
  }
}
